import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemedCalendar } from '../Calendar/ThemedCalendar';
import { useDispatch, useSelector } from 'react-redux';
import { LiveOrLineType, StoreType } from '../../store/spirs/types';
import { SportLogoFilterSelector } from '../custom/SportLogoFilterSelector';
import { Sleep } from '../../utils/Sleep';
import {
    getTreeResultAsync,
    RETURN_FROM_RESULT,
    SET_RESULT_TAB
} from '../../store/spirs/actions';
import { RoundedTabs } from '../tabs/RoundedTabs';
import { UpButtonClear } from '../Line/UpButton';
import { Level } from '../../models/api/codegen/code';
import { TreeInit } from '../../store/spirs/reducers/treeReducer';
import { buildVisibleRows } from '../tree/rowBuilder';
import { ResultTreeRow, RowProps } from '../tree/result/ResultTreeRow';
import { List, useDynamicRowHeight, useListRef } from 'react-window';

export const Results: React.FC<{type: 'ONLY_RESULTS' | 'ALL'}> = ({type}) => {
    const { t } = useTranslation();
    const [selectedDate, setSelectedDate] = React.useState(new Date());
    const [sportId, selectSportId] = React.useState<number | undefined>();
    const {
        selectResultTab: currentTab,
        spirs: { result: tree, sportAndChlInResults }
    } = useSelector((state: StoreType) => state);

    const listRefV = useListRef(null);
    const [visibleLevels, setVisibleLevels] = useState<Level[]>([])
    const [localTree, setLocalTree] = useState(TreeInit);
    const [expandedSports, setExpandedSports] = useState<Set<number>>(() => new Set())
    const [height, setHeight] = useState<number>(window.innerHeight);
    const [minShowIndex, setMinShowIndex] = React.useState(0);
    const rowHeight = useDynamicRowHeight({ defaultRowHeight: 50 });

    const toggleSport = React.useCallback((sportId: number) => {
        setExpandedSports(prev => {
            const next = new Set(prev);
            next.has(sportId) ? next.delete(sportId) : next.add(sportId);
            return next;
        })
    }, [])

    React.useEffect(() => {
        const onResize = () => setHeight(window.innerHeight);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const onRowsRendered = (visibleRows: { startIndex: number; stopIndex: number; }, allRows: { startIndex: number; stopIndex: number; }) => {
        setMinShowIndex(visibleRows.startIndex)
    }

    const onClick = () => {
        const list = listRefV.current;
        list?.scrollToRow({
            align: "auto", // optional
            behavior: "auto", // optional
            index: 0
        });
    };

    React.useEffect(() => {
        const next = buildVisibleRows(tree.l, expandedSports, sportId)
        setVisibleLevels(next);
        setLocalTree(tree)
    }, [tree.v, expandedSports, sportId])

    const minDate = new Date();
    minDate.setDate(minDate.getDate() - 14);
    const dispatch = useDispatch();


    const getTree = (date: Date, mode: LiveOrLineType) => {
        dispatch(
            getTreeResultAsync.request({
                status: mode,
                startDate: date,
                ...sportAndChlInResults
            })
        );
    };

    React.useEffect(() => {
        Sleep(100).then(() => getTree(selectedDate, currentTab));
        return () => {
            dispatch(RETURN_FROM_RESULT());
        };
    }, []);

    React.useEffect(() => {
        const interval = setInterval(
            () => getTree(selectedDate, currentTab),
            60000
        );
        return () => clearInterval(interval);
    }, [currentTab, selectedDate]);

    function handleChangeDate(d: Date) {
        Promise.resolve()
            .then(() => setSelectedDate(d))
            .then(() => Sleep(500).then(() => getTree(d, currentTab)));
    }

    function handleChange(newValue: LiveOrLineType) {
        Promise.resolve()
            .then(() => dispatch(SET_RESULT_TAB(newValue)))
            .then(() => newValue === 'LIVE' && setSelectedDate(new Date()))
            .then(() => Sleep(500).then(() => getTree(selectedDate, newValue)));
    }

    const handleSelectSport = (sportId: number | undefined) =>
        selectSportId(sportId);


    const tabs = [
        {
            value: 'LIVE_AND_FINISHED' as LiveOrLineType,
            label: t('results.all.tabTitle')
        },
        {
            value: 'FINISHED' as LiveOrLineType,
            label: t('results.finished.tabTitle')
        },
        { value: 'LIVE' as LiveOrLineType, label: t('results.live.tabTitle') }
    ];
    return (
        <div className="py-0 px-0 tablet:p-0 bg-[#272d33] text-white">
            <div className={`flex justify-between flex-col-reverse tablet:flex-row p-0 tablet:gap-[10px] ${type === 'ONLY_RESULTS' ? 'pr-[60px]' : ''}`}>
                <RoundedTabs<LiveOrLineType>
                    value={currentTab}
                    onChange={handleChange}
                    tabs={tabs}
                />
                <div className="flex justify-between">
                    <div className="tablet:hidden font-bold mobile:px-4">
                        <h2>{t('verticalMenuItems.results')}</h2>
                    </div>

                    <div className='content-center mobile:px-4'>
                        <ThemedCalendar
                            selectedDate={selectedDate}
                            callback={handleChangeDate}
                            disabled={currentTab === 'LIVE'}
                            minDate={minDate}
                        />
                    </div>
                </div>
            </div>
            <div className="relative w-full">
                <SportLogoFilterSelector
                    sports={tree.s}
                    callback={handleSelectSport}
                    sportId={sportId}
                    page={'Result'}
                />
                <List<RowProps>
                    onRowsRendered={onRowsRendered}
                    style={{height: height - 250}}
                    listRef={listRefV}
                    rowComponent={ResultTreeRow}
                    rowCount={visibleLevels.length}
                    rowHeight={rowHeight}
                    rowProps={{visibleLevels, toggleSport, tree: localTree}}
                />
                {<UpButtonClear visible={minShowIndex > 0} onClick={onClick}/>}
            </div>
        </div>
    );
};

export { Results as default };
